import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Input, ViewChild, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { EventTimelineSyncService } from '@app/v2/event/services/event-timeline-sync.service'
import { PersonaService } from '@app/v2/personas/services/persona.service'
import { Persona } from '@app/v2/personas/types/persona.type'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { Observable, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, map, of, switchMap } from 'rxjs'
import { EventService } from '../../../../services/event.service'
import { EventWrapperModel } from '../../../../types/event-wrapper-model.type'
import { Event, EventTimelineFilterKey } from '../../../../types/event.type'
import { EventWrapperComponent } from '../../../event-wrapper/event-wrapper.component'
import { EventsTimelineEmptyStateComponent } from '../events-timeline-empty-state/events-timeline-empty-state.component'
import { EventsTimelineHeaderComponent } from '../events-timeline-header/events-timeline-header.component'
import { EventsTimelineSkeletonCardComponent } from '../events-timeline-skeleton-card/events-timeline-skeleton-card.component'

const DEFAULT_SCROLL_PADDING_TOP = 8

@Component({
  selector: 'app-archived-events-timeline',
  standalone: true,
  imports: [ReactiveFormsModule, EventWrapperComponent, EventsTimelineEmptyStateComponent, EventsTimelineSkeletonCardComponent, EventsTimelineHeaderComponent],
  providers: [EventService, PersonaService, EventTimelineSyncService],
  templateUrl: './archived-events-timeline.component.html',
  styleUrl: './archived-events-timeline.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArchivedEventsTimelineComponent {
  @ViewChild('archivedLoadMoreTrigger')
  public set archivedLoadMoreTriggerRef(trigger: ElementRef<HTMLDivElement> | undefined) {
    if (!trigger) return
    this._archivedInfiniteObserver.disconnect()
    this._archivedInfiniteObserver.observe(trigger.nativeElement)
  }
  @ViewChild('archivedEventsTimelineHeader', { read: ElementRef<HTMLElement> }) archivedEventsTimelineHeaderRef!: ElementRef<HTMLElement>

  @Input() public set archivedCount(value: number) {
    const nextCount = Math.max(0, value ?? 0)
    this._archivedCount.set(nextCount)

    if (nextCount === 0) {
      this._applyNoArchivedState()
      return
    }

    this._triggerInitialFetchIfNeeded()
  }

  public readonly events = signal<Event[]>([])
  public readonly isInitialLoading = signal(true)
  public readonly isPaginating = signal(false)
  public readonly hasMore = signal(true)
  public readonly loadingCounts = signal(false)
  public readonly loadingInitialPersonas = signal(false)
  public readonly isModalOpen = signal(false)
  public readonly currentPage = signal(1)
  public readonly searchControl = new FormControl<string>('', { nonNullable: true })
  public readonly bannerSaveInProgressEventId = signal<number | null>(null)
  private readonly _archivedCount = signal(0)
  private _initialFetchTriggered = false
  private readonly _personaPerPage = 5

  public readonly eventPersonasByEventId = signal<Record<number, Persona[]>>({})
  public readonly eventPersonasTotalByEventId = signal<Record<number, number | null>>({})
  public readonly loadingPersonasByEventId = signal<Record<number, boolean>>({})
  public readonly scrollPaddingTop = signal<number>(DEFAULT_SCROLL_PADDING_TOP)

  public readonly headerTitle = computed(() => `Archived events (${this._archivedCount()})`)

  private readonly _perPage = 5
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _eventService = inject(EventService)
  private readonly _personaService = inject(PersonaService)
  private readonly _authService = inject(AuthService)
  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _alertService = inject(AlertService)
  private readonly _archivedInfiniteObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return
      this.loadNext()
    },
    { rootMargin: '220px', threshold: 0.01 }
  )

  /**
   * Resolve archived empty-state mode based on search query.
   */
  public getEmptyStateMode(): 'filtered' | 'search' {
    return this.searchControl.value.trim() ? 'search' : 'filtered'
  }

  constructor() {
    // Inputs arrive after ctor; initial fetch triggered from `archivedCount` setter.
    this._initializeSearchFlow()
  }

  ngOnInit(): void {
    this._triggerInitialFetchIfNeeded()
  }

  /**
   * Handle archived header debounced search change.
   */
  private _initializeSearchFlow(): void {
    this.searchControl.valueChanges
      .pipe(
        map((search) => (search ?? '').trim()),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((search) => {
          this.currentPage.set(1)
          if (this._archivedCount() === 0) {
            this._applyNoArchivedState()
            return of(void 0)
          }
          return this._fetchArchivedEvents({ page: 1, reset: true, search })
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe()
  }

  public onArchivedCriteriaChanged(criteria: { filter: EventTimelineFilterKey; search: string }): void {
    const search = String(criteria?.search ?? '').trim()
    this.searchControl.setValue(search, { emitEvent: false })
    this.currentPage.set(1)
    this._fetchArchivedEvents({ page: 1, reset: true, search }).pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Load next archived events page for infinite list.
   */
  public loadNext(): void {
    if (this._archivedCount() === 0) return
    if (this.isPaginating() || this.isInitialLoading() || !this.hasMore() || this.isModalOpen()) return
    const nextPage = this.currentPage() + 1
    this.currentPage.set(nextPage)
    this._fetchArchivedEvents({ page: nextPage, reset: false, search: this.searchControl.value.trim() }).pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Trigger initial fetch if needed.
   */
  private _triggerInitialFetchIfNeeded(): void {
    if (this._initialFetchTriggered) return
    if (this._archivedCount() === 0) {
      this._applyNoArchivedState()
      return
    }

    this._initialFetchTriggered = true
    this._fetchArchivedEvents({ page: 1, reset: true, search: '' }).pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Apply no archived state.
   */
  private _applyNoArchivedState(): void {
    this.events.set([])
    this.currentPage.set(1)
    this.hasMore.set(false)
    this.isInitialLoading.set(false)
    this.isPaginating.set(false)
  }

  /**
   * Fetch archived events and merge pagination response.
   */
  private _fetchArchivedEvents(options: { page: number; reset: boolean; search: string }) {
    if (options.reset) {
      this.isInitialLoading.set(true)
    } else {
      this.isPaginating.set(true)
    }

    return this._eventService
      .getEventsList({
        organizationId: this._authService.currentWorkspace.id,
        page: options.page,
        perPage: this._perPage,
        search: options.search,
        isArchived: true
      })
      .pipe(
        switchMap((response) =>
          this._decorateEvents(response.data).pipe(
            map((decoratedEvents) => ({
              ...response,
              data: decoratedEvents
            }))
          )
        ),
        finalize(() => {
          this.isInitialLoading.set(false)
          this.isPaginating.set(false)
        }),
        switchMap((response) => {
          const currentEvents = options.reset ? [] : this.events()
          const merged = options.reset ? response.data : [...currentEvents, ...response.data]
          this.events.set(merged)
          this.hasMore.set(response.current_page < response.last_page)
          return this._loadEventPersonas(merged, options.reset).pipe(map(() => void 0))
        })
      )
  }

  /**
   * Enrich events with registration counts.
   */
  private _decorateEvents(events: Event[]) {
    if (!events.length) return of(events)

    this.loadingCounts.set(true)
    return this._eventService.getEventCounts(events).pipe(finalize(() => this.loadingCounts.set(false)))
  }

  /**
   * Fetch personas for visible archived events list.
   * @param {Event[]} events - The events to fetch personas for.
   * @param {boolean} reset - Whether to reset the personas loading state.
   * @returns {Observable<void>} - An observable that emits when the personas are loaded.
   */
  private _loadEventPersonas(events: Event[], reset: boolean): Observable<void> {
    if (reset) {
      this.loadingInitialPersonas.set(false)
      this.eventPersonasByEventId.set({})
      this.eventPersonasTotalByEventId.set({})
      this.loadingPersonasByEventId.set({})
    }

    if (!events.length) {
      this.loadingInitialPersonas.set(false)
      return of(void 0)
    }

    const eventIds = new Set(events.map((event) => event.id))
    const currentLoading: Record<number, boolean> = {}
    events.forEach((event) => {
      currentLoading[event.id] = this.loadingPersonasByEventId()[event.id] ?? false
    })
    this.loadingPersonasByEventId.set(currentLoading)

    const eventsToFetch = events.filter((event) => !Object.prototype.hasOwnProperty.call(this.eventPersonasByEventId(), event.id))
    if (!eventsToFetch.length) {
      this.loadingInitialPersonas.set(false)
      return of(void 0)
    }

    if (reset) {
      this.loadingInitialPersonas.set(true)
    }

    const requests = eventsToFetch.map((event) => {
      this.loadingPersonasByEventId.set({ ...this.loadingPersonasByEventId(), [event.id]: true })
      return this._personaService.getPersonas(event.workspace_id, event.id, this._personaPerPage, 1).pipe(
        map((response) => ({ eventId: event.id, personas: response.data ?? [], total: response.total ?? null })),
        catchError(() => of({ eventId: event.id, personas: [] as Persona[], total: 0 }))
      )
    })

    return forkJoin(requests).pipe(
      map((results) => {
        const personasByEventId = { ...this.eventPersonasByEventId() }
        const totalsByEventId = { ...this.eventPersonasTotalByEventId() }
        const loadingByEventId = { ...this.loadingPersonasByEventId() }

        results.forEach((result) => {
          if (!eventIds.has(result.eventId)) return
          personasByEventId[result.eventId] = result.personas
          totalsByEventId[result.eventId] = result.total
          loadingByEventId[result.eventId] = false
        })

        this.eventPersonasByEventId.set(personasByEventId)
        this.eventPersonasTotalByEventId.set(totalsByEventId)
        this.loadingPersonasByEventId.set(loadingByEventId)
        this.loadingInitialPersonas.set(false)
        return void 0
      })
    )
  }

  /**
   * Handle card modal open/close state to pause pagination trigger.
   */
  public onModalStateChanged(isOpen: boolean): void {
    this.isModalOpen.set(isOpen)
  }

  /**
   * Get the event wrapper model
   * @param event {Event} - The event
   * @param isLast {boolean} - Whether the event is the last one
   * @returns {EventWrapperModel} - The event wrapper model
   */
  public getEventWrapperModel(event: Event, isLast: boolean): EventWrapperModel {
    const personas = this.eventPersonasByEventId()[event.id] ?? []
    const personasTotal = this.eventPersonasTotalByEventId()[event.id] ?? null
    const personasLoading = this.loadingPersonasByEventId()[event.id] ?? false

    return this._eventService.buildArchivedTimelineWrapperModel({
      event,
      isLast,
      events: this.events(),
      loadingCounts: this.loadingCounts(),
      bannerSaveInProgress: this.bannerSaveInProgressEventId() === event.id,
      personas,
      personasTotal,
      personasLoading,
      createPersonaPending: false,
      createPersonaApiError: null
    })
  }

  /**
   * Restore archived event then remove from list.
   */
  public onRestoreConfirmed(event: Event): void {
    if (!this._workspaceRoleService.canRestoreEvent()) return
    this._eventService
      .updateEventData(event.workspace_id, event.id, { is_archived: false } as Partial<Event>)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response: Response<Event>) => {
          this._alertService.alert(Alert.SUCCESS, response.message)
          this.events.set(this.events().filter((currentEvent) => currentEvent.id !== event.id))
          this._archivedCount.set(Math.max(0, this._archivedCount() - 1))
        },
        error: (error: { error?: { message?: string }; message?: string }) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message || error?.message || 'Failed to restore event')
        }
      })
  }

  /**
   * Remove deleted event from archived list and update count.
   */
  public onDeletePermanentlySucceeded(event: Event): void {
    this.events.set(this.events().filter((currentEvent) => currentEvent.id !== event.id))
    this._archivedCount.set(Math.max(0, this._archivedCount() - 1))
  }

  /**
   * Cleanup archived observer on destroy.
   */
  public ngOnDestroy(): void {
    this._archivedInfiniteObserver.disconnect()
  }

  /**
   * Handle the display of the floating header.
   * @param isDisplaying - Whether the header is displaying.
   */
  public onDisplayFloatingHeader(isDisplaying: boolean): void {
    this.scrollPaddingTop.set(isDisplaying ? DEFAULT_SCROLL_PADDING_TOP + this.archivedEventsTimelineHeaderRef?.nativeElement?.clientHeight : DEFAULT_SCROLL_PADDING_TOP)
  }
}
